import { getOriginalDoc } from "@codemirror/merge";
import {
  EditorState as CMEditorState,
  Compartment,
  Extension,
  Facet,
  StateEffect,
  StateField,
} from "@codemirror/state";
import {
  Decoration,
  DecorationSet,
  EditorView,
  ViewPlugin,
  ViewUpdate,
  WidgetType,
} from "@codemirror/view";
import React from "react";
import { createRoot } from "react-dom/client";
import type { Root } from "react-dom/client";
import createDomElement from "utils/createDomElement";

import {
  DocumentAiState,
  DocumentAiStateUpdate,
} from "pages/DevelopWorkspace/workspace.types";

import AiAssistantWidgetView from "./AiAssistantWidgetView";

export type Callbacks = {
  onUpdatePanel: (updates: Partial<DocumentAiStateUpdate>) => void;
  onClose: () => void;
  onAction: (userPrompt: string) => void;
  onStop: () => void;
  onAcceptAll: () => void;
  onRejectAll: () => void;
};

export const setAiAssistantState = StateEffect.define<DocumentAiState | null>();
export const focusAiAssistant = StateEffect.define<null>();

type AiAssistantInternalState = {
  decorations: DecorationSet;
  pos: number | null;
  aiState: DocumentAiState | null;
  widgetInstance: AiAssistantWidget | null;
};

type AiAssistantConfig = {
  callbacks: Callbacks;
  documentId: string;
  isMergeMode?: boolean;
};

const defaultAiAssistantConfig: AiAssistantConfig = {
  documentId: "",
  isMergeMode: false,
  callbacks: {
    onUpdatePanel: (_updates: Partial<DocumentAiStateUpdate>) => {},
    onClose: () => {},
    onAction: () => {},
    onStop: () => {},
    onAcceptAll: () => {},
    onRejectAll: () => {},
  },
};

export const aiAssistantConfigFacet = Facet.define<
  AiAssistantConfig,
  AiAssistantConfig
>({
  combine(values) {
    return values.length ? values[values.length - 1] : defaultAiAssistantConfig;
  },
});

export const aiAssistantConfigCompartment = new Compartment();
export const aiAssistantLayoutNudgeCompartment = new Compartment();

function isMergeActive(state: CMEditorState): boolean {
  try {
    const orig = getOriginalDoc(state);
    return !!orig && orig.toString() !== state.doc.toString();
  } catch {
    return false;
  }
}

function scheduleFocus(view: EditorView, tries = 0) {
  const textarea = view.dom.querySelector(
    ".cm-ai-assistant-textarea"
  ) as HTMLTextAreaElement | null;
  if (textarea && !textarea.disabled) {
    textarea.focus();
    const end = textarea.value.length;
    if (typeof textarea.setSelectionRange === "function") {
      textarea.setSelectionRange(end, end);
    }
    return;
  }
  if (tries < 2) {
    requestAnimationFrame(() => scheduleFocus(view, tries + 1));
  }
}

class AiAssistantWidget extends WidgetType {
  public callbacks: Callbacks;

  public aiState: DocumentAiState;

  public container: HTMLDivElement | null = null;

  private resizeObserver: ResizeObserver | null = null;

  private editorView: EditorView | null = null;

  public mergeActive: boolean = false;

  private reactRoot: Root | null = null;

  constructor(aiState: DocumentAiState, callbacks: Callbacks) {
    super();
    this.aiState = aiState;
    this.callbacks = callbacks;
  }

  private setMergeActiveFromState(state: CMEditorState) {
    this.mergeActive = isMergeActive(state);
  }

  private requestLayoutMeasure() {
    const view = this.editorView;
    if (!view) return;
    try {
      view.requestMeasure({
        key: "aiAssistantWidgetLayout",
        read: () => {
          return null;
        },
        write: () => {},
      });
      // Nudge via no-op reconfiguration to ensure gutters resync
      view.dispatch({
        effects: aiAssistantLayoutNudgeCompartment.reconfigure(
          EditorView.theme({})
        ),
      });
      view.dispatch({
        effects: aiAssistantLayoutNudgeCompartment.reconfigure([]),
      });
    } catch {
      console.error("Error requesting layout measure");
    }
  }

  toDOM(view: EditorView): HTMLElement {
    this.editorView = view;
    this.setMergeActiveFromState(view.state);
    const container = createDomElement("div", {
      className: `cm-ai-assistant-widget`,
      "data-testid": "ai-assistant-widget",
    });
    this.container = container;
    this.reactRoot = createRoot(container);
    this.renderReact();
    return container;
  }

  ignoreEvent(): boolean {
    return true;
  }

  updateAiState(next: DocumentAiState) {
    this.aiState = next;
    this.renderReact();
    requestAnimationFrame(() => this.requestLayoutMeasure());
  }

  updateCallbacks(next: Callbacks) {
    this.callbacks = next;
  }

  destroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.reactRoot) {
      const root = this.reactRoot;
      this.reactRoot = null;
      const container = this.container;
      const doUnmount = () => {
        try {
          root.unmount();
        } finally {
          if (container && container.isConnected) {
            try {
              container.remove();
            } catch {
              console.error("Error removing container");
            }
          }
        }
      };
      try {
        if (typeof queueMicrotask === "function") {
          queueMicrotask(doUnmount);
        } else if (typeof requestAnimationFrame === "function") {
          requestAnimationFrame(() => doUnmount());
        } else {
          setTimeout(doUnmount, 0);
        }
      } catch {
        setTimeout(doUnmount, 0);
      }
    }
    this.container = null;
    this.resizeObserver = null;
    this.editorView = null;
  }

  renderReact() {
    if (!this.reactRoot || !this.container) return;
    const {
      onUpdatePanel,
      onClose,
      onAction,
      onStop,
      onAcceptAll,
      onRejectAll,
    } = this.callbacks;
    const requestMeasure = () => this.requestLayoutMeasure();
    this.reactRoot.render(
      React.createElement(AiAssistantWidgetView, {
        aiState: this.aiState,
        mergeActive: this.mergeActive,
        onUpdatePanel,
        onClose,
        onAction,
        onStop,
        onAcceptAll,
        onRejectAll,
        requestMeasure,
      })
    );
  }
}

export const aiAssistantStateField =
  StateField.define<AiAssistantInternalState>({
    create: (): AiAssistantInternalState => ({
      decorations: Decoration.none,
      pos: null,
      aiState: null,
      widgetInstance: null,
    }),
    update(value, tr) {
      const { decorations, pos, aiState, widgetInstance } = value;
      const cfg = tr.state.facet(aiAssistantConfigFacet);
      const callbacks = cfg.callbacks;

      const nextDecorations = tr.docChanged
        ? decorations.map(tr.changes)
        : decorations;
      const nextPos =
        tr.docChanged && pos !== null ? tr.changes.mapPos(pos) : pos;

      // Keep widget callbacks in sync
      if (widgetInstance && callbacks) {
        widgetInstance.updateCallbacks(callbacks);
      }

      const setStateEffect = tr.effects.find(e => e.is(setAiAssistantState));
      if (setStateEffect && setStateEffect.is(setAiAssistantState)) {
        const nextAiState = setStateEffect.value;

        if (!nextAiState) {
          return {
            decorations: Decoration.none,
            pos: null,
            aiState: null,
            widgetInstance: null,
          };
        }

        const docLength = tr.state.doc.length;
        const desiredPos = nextAiState.widgetPosition;
        const clampedPos = Math.max(0, Math.min(desiredPos, docLength));
        if (widgetInstance && nextPos === clampedPos) {
          widgetInstance.updateAiState(nextAiState);
          return {
            decorations: nextDecorations,
            pos: nextPos,
            aiState: nextAiState,
            widgetInstance,
          };
        }

        const newWidget = new AiAssistantWidget(nextAiState, callbacks);
        const deco = Decoration.widget({
          widget: newWidget,
          block: true,
          side: -1,
        });
        return {
          decorations: Decoration.set([deco.range(clampedPos)]),
          pos: clampedPos,
          aiState: nextAiState,
          widgetInstance: newWidget,
        };
      }

      return {
        decorations: nextDecorations,
        pos: nextPos,
        aiState,
        widgetInstance,
      };
    },
    provide: f =>
      EditorView.decorations.compute([f], state => state.field(f).decorations),
  });

const aiAssistantFocusPlugin = ViewPlugin.define((_view: EditorView) => ({
  update(update: ViewUpdate) {
    const requested = update.transactions.some(tr =>
      tr.effects.some(e => e.is(focusAiAssistant))
    );
    if (requested) {
      scheduleFocus(update.view);
    }
  },
}));

// Keep external aiState positions in sync with document edits and update button labels
const aiAssistantSyncPlugin = ViewPlugin.define((_view: EditorView) => ({
  update(update: ViewUpdate) {
    const internal = update.state.field(aiAssistantStateField, false);
    const widget = internal?.widgetInstance;
    if (widget) {
      const mergeActive = isMergeActive(update.state);
      if (widget.mergeActive !== mergeActive) {
        widget.mergeActive = mergeActive;
        widget.renderReact?.();
      }
    }
  },
}));

export function aiAssistantWidgetExtension(
  documentId: string,
  callbacks: Callbacks
): Extension[] {
  return [
    aiAssistantConfigCompartment.of(
      aiAssistantConfigFacet.of({ documentId, callbacks })
    ),
    // Include layout nudge compartment so requestLayoutMeasure can reconfigure it
    aiAssistantLayoutNudgeCompartment.of([]),
    aiAssistantStateField,
    aiAssistantFocusPlugin,
    aiAssistantSyncPlugin,
  ];
}
